import { Modal, Typography } from 'antd';
import type {
  CsvColumnDefinition,
  ParsedCsvRow,
} from '~/app/components/knowledgeBase/componentSchemaManager/ComponentSchemaManager.types';
import { assetImportCsvConfig } from '../../assetImportCsvConfig';

const { Paragraph, Text } = Typography;

const SUGGESTION_FIELD_ALIASES: Record<string, string> = {
  name: 'assetName',
  siteAddress: 'siteAdress',
};

export function normalizeSuggestions(
  suggestions: Record<string, string>,
): Record<string, string> {
  const validKeys = new Set(
    assetImportCsvConfig.columns.map((column) => column.key),
  );

  return Object.fromEntries(
    Object.entries(suggestions).flatMap(([header, value]) => {
      const normalizedValue = SUGGESTION_FIELD_ALIASES[value] ?? value;
      return validKeys.has(normalizedValue) ? [[header, normalizedValue]] : [];
    }),
  );
}

export function filterSuggestionsByHeaders(
  suggestions: Record<string, string>,
  headers: string[],
): Record<string, string> {
  return Object.fromEntries(
    headers.flatMap((header) => {
      const mappedField = suggestions[header];
      return mappedField ? [[header, mappedField]] : [];
    }),
  );
}

export function buildPreviewColumns(params: {
  suggestions: Record<string, string>;
  headers: string[];
}): CsvColumnDefinition[] {
  const { suggestions, headers } = params;
  const unmappedHeaders = headers.filter((header) => !suggestions[header]);

  return [
    ...assetImportCsvConfig.columns,
    ...unmappedHeaders.map((header) => ({
      key: `__unmapped__${header}`,
      title: `${header} (unmapped)`,
      required: false,
    })),
  ];
}

export function buildPreviewRows(params: {
  headers: string[];
  rows: string[][];
  suggestions: Record<string, string>;
}): ParsedCsvRow[] {
  const { headers, rows, suggestions } = params;

  return rows.map((row) => {
    const parsedRow: ParsedCsvRow = {};

    headers.forEach((header, index) => {
      const targetField = suggestions[header];

      if (!targetField) {
        parsedRow[`__unmapped__${header}`] = row[index] ?? '';
        return;
      }

      parsedRow[targetField] = row[index] ?? '';
    });

    return parsedRow;
  });
}

export function buildUploadRows(params: {
  headers: string[];
  rows: string[][];
  suggestions: Record<string, string>;
}): ParsedCsvRow[] {
  return buildPreviewRows(params).map((row) =>
    Object.fromEntries(
      Object.entries(row).filter(([key]) => !key.startsWith('__unmapped__')),
    ),
  );
}

export function buildMappedFileName(fileName: string): string {
  const trimmedFileName = fileName.trim();

  if (trimmedFileName.length === 0) {
    return 'asset-import-mapped.csv';
  }

  return trimmedFileName.replace(/\.[^./\\]+$/, '') + '-mapped.csv';
}

export function buildAvailableFields(): string[] {
  return assetImportCsvConfig.columns.map((column) => column.key);
}

export function buildNormalizedHeaders(params: {
  headers: string[];
  suggestions: Record<string, string>;
  fallbackRows: ParsedCsvRow[];
}): string[] {
  const normalizedHeaders = params.headers.flatMap((header) => {
    const normalizedHeader = params.suggestions[header];

    return normalizedHeader ? [normalizedHeader] : [];
  });

  if (normalizedHeaders.length > 0) {
    return Array.from(new Set(normalizedHeaders));
  }

  return Object.keys(params.fallbackRows[0] ?? {});
}

export function askToRegenerateMapping(
  missingHeaders: string[],
): Promise<boolean> {
  return new Promise((resolve) => {
    let resolved = false;

    const settle = (value: boolean) => {
      if (resolved) {
        return;
      }

      resolved = true;
      resolve(value);
    };

    Modal.confirm({
      title: 'New fields detected',
      content: (
        <div>
          <Paragraph>
            The following CSV fields are not part of the saved mapping:
          </Paragraph>
          <Paragraph>
            <Text>{missingHeaders.join(', ')}</Text>
          </Paragraph>
          <Paragraph>
            Would you like to regenerate the mapping for this file?
          </Paragraph>
        </div>
      ),
      okText: 'Regenerate mapping',
      cancelText: 'Keep saved mapping',
      onOk: () => settle(true),
      onCancel: () => settle(false),
      afterClose: () => settle(false),
    });
  });
}
